import { useEffect, useMemo, useState } from "react";
import { taskService } from "@miplatform/api";
import {
  DATE_TIME_FORMATS,
  HTTP_STATUSES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IAutoAssignmentWindowResponse,
  ICalendarWorkTemplateBase,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { useDetailedCalendarSet } from "@mibuildweb/hooks";
import { getOverlappingWindowIndices } from "../../../Config/WorkOrderTemplates/configWorkOrderTemplates.util";

const START = "start";
const END = "end";

export type DraftWindow = {
  id: string;
  start: string | null;
  end: string | null;
};

type DraftWindowsByTemplateId = Record<string, DraftWindow[]>;

const defaultWindow = (): DraftWindow => ({
  id: createUUID(),
  start: null,
  end: null,
});

const cloneWindows = (
  windows: IAutoAssignmentWindowResponse[] | DraftWindow[] = []
): DraftWindow[] =>
  windows.map((window) => ({
    id: (window as DraftWindow)?.id ?? createUUID(),
    start: window.start ?? null,
    end: window.end ?? null,
  }));

const buildDraftFromTemplates = (
  workTemplateBases: ICalendarWorkTemplateBase[]
): DraftWindowsByTemplateId =>
  workTemplateBases.reduce<DraftWindowsByTemplateId>((acc, template) => {
    acc[template.id] = cloneWindows(template.autoAssignWindows ?? []);
    return acc;
  }, {});

const normalizeTimeValue = (formattedValue: string): string =>
  /^\d{2}:\d{2}$/.test(formattedValue)
    ? `${formattedValue}:00`
    : formattedValue;

const windowsEqual = (a: DraftWindow[], b: DraftWindow[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every(
    (window, index) =>
      (window.start ?? null) === (b[index]?.start ?? null) &&
      (window.end ?? null) === (b[index]?.end ?? null)
  );
};

const hasIncompleteWindows = (windows: DraftWindow[]): boolean =>
  windows.some((window) => !window.start || !window.end);

export const useEditTimeWindowsModalViewModel = ({
  open,
  workTemplateBases,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  workTemplateBases: ICalendarWorkTemplateBase[];
  onSuccess?: () => void;
}) => {
  const { params } = useRouter();
  const { refreshCalendarSet } = useDetailedCalendarSet(params.calendarSetId, {
    auto: false,
  });
  const [draftWindowsByTemplateId, setDraftWindowsByTemplateId] =
    useState<DraftWindowsByTemplateId>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && Object.keys(draftWindowsByTemplateId).length === 0) {
      setDraftWindowsByTemplateId(buildDraftFromTemplates(workTemplateBases));
    }
  }, [open, workTemplateBases, draftWindowsByTemplateId]);

  const overlappingByTemplateId = useMemo(() => {
    return workTemplateBases.reduce<Record<string, Set<number>>>(
      (acc, template) => {
        acc[template.id] = getOverlappingWindowIndices(
          draftWindowsByTemplateId[template.id] ?? []
        );
        return acc;
      },
      {}
    );
  }, [workTemplateBases, draftWindowsByTemplateId]);

  const dirtyTemplateIds = useMemo(() => {
    return workTemplateBases
      .filter((template) => {
        const draft = draftWindowsByTemplateId[template.id] ?? [];
        const original = cloneWindows(template.autoAssignWindows ?? []);
        return !windowsEqual(draft, original);
      })
      .map((template) => template.id);
  }, [workTemplateBases, draftWindowsByTemplateId]);

  const hasValidationErrors = useMemo(() => {
    return workTemplateBases.some((template) => {
      const windows = draftWindowsByTemplateId[template.id] ?? [];
      return (
        hasIncompleteWindows(windows) ||
        (overlappingByTemplateId[template.id]?.size ?? 0) > 0
      );
    });
  }, [workTemplateBases, draftWindowsByTemplateId, overlappingByTemplateId]);

  const handleClose = () => {
    if (isSaving) return;
    setDraftWindowsByTemplateId({});
    onClose();
  };

  const updateTemplateWindows = (
    templateId: string,
    windows: DraftWindow[]
  ) => {
    setDraftWindowsByTemplateId((prev) => ({
      ...prev,
      [templateId]: windows,
    }));
  };

  const handleAddWindow = (templateId: string) => {
    const current = draftWindowsByTemplateId[templateId] ?? [];
    updateTemplateWindows(templateId, [...current, defaultWindow()]);
  };

  const handleRemoveWindow = (templateId: string, index: number) => {
    const current = draftWindowsByTemplateId[templateId] ?? [];
    updateTemplateWindows(
      templateId,
      current.filter((_, windowIndex) => windowIndex !== index)
    );
  };

  const handleWindowChange = (
    templateId: string,
    index: number,
    field: "start" | "end",
    formattedValue: string
  ) => {
    const current = draftWindowsByTemplateId[templateId] ?? [];
    const nextWindows = [...current];
    nextWindows[index] = {
      ...(nextWindows[index] ?? defaultWindow()),
      [field]: normalizeTimeValue(formattedValue),
    };
    updateTemplateWindows(templateId, nextWindows);
  };

  const getWindowFields = (templateId: string, index: number) => {
    const timeWindow =
      draftWindowsByTemplateId[templateId]?.[index] ?? defaultWindow();

    return [
      {
        label: "Start Time",
        name: `${templateId}.${index}.start`,
        fieldType: INPUT_TYPES.TIME_PICKER,
        format: DATE_TIME_FORMATS.FORMAT_24_HOUR,
        value: timeWindow.start ?? null,
        handleChange: (formattedValue: string) => {
          handleWindowChange(templateId, index, START, formattedValue);
        },
        disabled: isSaving,
        width: "50%",
      },
      {
        label: "End Time",
        name: `${templateId}.${index}.end`,
        fieldType: INPUT_TYPES.TIME_PICKER,
        format: DATE_TIME_FORMATS.FORMAT_24_HOUR,
        value: timeWindow.end ?? null,
        handleChange: (formattedValue: string) => {
          handleWindowChange(templateId, index, END, formattedValue);
        },
        disabled: isSaving,
        width: "50%",
      },
    ];
  };

  const handleSave = async () => {
    if (dirtyTemplateIds.length === 0 || hasValidationErrors || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const dirtyTemplates = workTemplateBases.filter((template) =>
        dirtyTemplateIds.includes(template.id)
      );

      const updatePromises = dirtyTemplates.map((template) => {
        const windows = (draftWindowsByTemplateId[template.id] ?? [])
          .filter((window) => Boolean(window.start && window.end))
          .map(({ start, end }) => ({ start, end }));

        return taskService.workOrderTemplates.updateAutoAssignmentWindows({
          id: template.id,
          data: {
            autoAssignmentWindows: windows as IAutoAssignmentWindowResponse[],
            allDayAssignment: windows.length === 0,
            disableCollisionChecks: true,
            modifiedOn: template?.modifiedOn,
          },
        });
      });

      const results = await Promise.allSettled(updatePromises);
      const allSucceeded = results.every(
        (result) =>
          result.status === "fulfilled" &&
          result.value.status === HTTP_STATUSES.OK
      );

      if (!allSucceeded) {
        toastService.error({
          text: "Failed to save time windows.",
        });
        return;
      }

      toastService.success({
        text: "Time windows saved successfully.",
      });
      refreshCalendarSet();
      setDraftWindowsByTemplateId({});
      onClose();
      onSuccess?.();
    } finally {
      setIsSaving(false);
    }
  };

  return {
    draftWindowsByTemplateId,
    overlappingByTemplateId,
    dirtyTemplateIds,
    hasValidationErrors,
    isSaving,
    handleClose,
    handleAddWindow,
    handleRemoveWindow,
    getWindowFields,
    handleSave,
  };
};
